import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Ticket, Clock, Plug, ArrowUpRight, Plus, TrendingUp } from "lucide-react";
import { useT } from "@/lib/i18n";

export default function Dashboard() {
  const t = useT();
  const [usage, setUsage] = useState(null);
  const [cards, setCards] = useState([]);

  useEffect(() => {
    api.get("/orgs/current").then(({ data }) => setUsage(data));
    api.get("/cards").then(({ data }) => setCards(data.cards.slice(0, 5)));
  }, []);

  const cardsUsed = usage?.usage?.cards_used ?? 0;
  const cardsLimit = usage?.usage?.cards_limit ?? 0;
  const pct = cardsLimit > 0 ? Math.min(100, (cardsUsed / cardsLimit) * 100) : 0;
  const totalSaved = cards.filter(c => c.report).reduce((a, c) => a + (c.report?.time_saved_minutes ?? 0), 0);

  return (
    <div className="p-8 lg:p-12 max-w-7xl">
      <div className="overline mb-3">{t("dash.overline")}</div>
      <div className="flex items-end justify-between mb-10">
        <h1 className="font-display font-black text-4xl tracking-tighter" data-testid="dashboard-title">{t("dash.title")}</h1>
        <Link to="/app/cards" className="btn-primary text-sm py-2 px-4" data-testid="dashboard-new-card-btn">
          <Plus className="w-4 h-4" /> {t("dash.analyze")}
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
        <KPI label={t("dash.kpi.cards")} value={cardsUsed} sub={`${t("dash.kpi.cardsOf")} ${cardsLimit === -1 ? "∞" : cardsLimit}`} Icon={Ticket} testid="kpi-cards" />
        <KPI label={t("dash.kpi.saved")} value={totalSaved} sub={t("dash.kpi.savedSub")} Icon={Clock} testid="kpi-saved" />
        <KPI label={t("dash.kpi.integrations")} value={usage?.usage?.integrations ?? 0} sub={t("dash.kpi.integrationsSub")} Icon={Plug} testid="kpi-integrations" />
        <KPI label={t("dash.kpi.team")} value={usage?.usage?.members ?? 0} sub={`${t("dash.kpi.teamMax")} ${usage?.usage?.members_limit === -1 ? "∞" : usage?.usage?.members_limit}`} Icon={TrendingUp} testid="kpi-team" />
      </div>

      <div className="surface p-6 mb-10" data-testid="usage-band">
        <div className="flex justify-between items-end mb-3">
          <div>
            <div className="overline mb-1">{t("dash.planUsage")}</div>
            <div className="text-lg">{usage?.plan?.name} <span className="text-white/40 text-sm font-mono">/{usage?.plan?.interval}</span></div>
          </div>
          <div className="font-mono text-sm text-white/60">
            <span className="text-white text-lg">{cardsUsed}</span> / {cardsLimit === -1 ? "∞" : cardsLimit} cards
          </div>
        </div>
        <div className="h-1.5 bg-white/10">
          <div className={`h-full transition-all ${pct >= 80 ? "bg-amber-400" : "bg-primary"}`} style={{ width: `${pct}%` }} />
        </div>
        {pct >= 80 && (
          <Link to="/app/billing" className="mt-4 inline-flex items-center gap-1 text-sm text-amber-400 hover:underline">
            {t("dash.upgrade")} <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>

      <div className="surface p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="overline">{t("dash.recent")}</div>
          <Link to="/app/cards" className="text-sm text-primary hover:underline">{t("dash.viewAll")}</Link>
        </div>
        {cards.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-white/40 mb-4 text-sm">{t("dash.noCards")}</div>
            <Link to="/app/cards" className="btn-primary inline-flex" data-testid="dashboard-first-card-btn">
              <Plus className="w-4 h-4" /> {t("dash.firstCard")}
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {cards.map((c) => (
              <li key={c.id}>
                <Link to={`/app/cards/${c.id}`} className="flex items-center gap-4 py-3 hover:bg-white/5 -mx-3 px-3" data-testid={`card-row-${c.id}`}>
                  <span className={`status-dot ${c.status === "complete" ? "bg-emerald-400" : "bg-amber-400 animate-pulse"}`} />
                  <span className="font-mono text-xs text-white/40 w-24 truncate">{c.external_id}</span>
                  <span className="flex-1 text-sm truncate">{c.title}</span>
                  <span className="font-mono text-xs text-white/50">{c.status}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, sub, Icon, testid }) {
  return (
    <div className="surface p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="overline">{label}</div>
        <Icon className="w-4 h-4 text-white/30" />
      </div>
      <div className="font-mono text-3xl font-bold">{value}</div>
      <div className="text-xs text-white/40 mt-1">{sub}</div>
    </div>
  );
}
